import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
  faMars,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { useEventId } from "../EventContext";
import { Button, InputField } from "@vionixasia/shared-components";
import { useIsAdmin } from "../utils/useIsAdmin";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  onSnapshot,
} from "firebase/firestore";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import { displayElapsed } from "../utils/finishTime";
import {
  FinishReceipt,
  printFinishReceipt,
} from "../components/FinishReceipt";


// Constants
const PAGE_SIZE = 100;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error(
    "VITE_API_BASE_URL is not defined. Please set it in the environment variables."
  );
}

// Convert nationality to ISO flag code and display name
// Format Gender with Icons (only for desktop)
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

// Helper to determine Age Group
const getAgeGroup = (age) => {
  if (age === null || age === undefined || age === "") return "Unknown";
  const numAge = parseInt(age);
  if (isNaN(numAge)) return "Unknown";

  if (numAge < 40) return "35-39";
  if (numAge <= 44) return "40-44";
  if (numAge <= 49) return "45-49";
  if (numAge <= 54) return "50-54";
  if (numAge <= 59) return "55-59";
  if (numAge <= 64) return "60-64";
  return "65+";
};

const AGE_GROUPS = ["35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "65+"];

// SkeletonRow Component for loading state: the same cells, visibility and
// rounded ends as the header, so it fills every column (Finish for admins too).
const SkeletonRow = React.memo(({ isAdmin }) => {
  const cells = [
    "rounded-l-card border-l",
    isAdmin ? "" : "rounded-r-card border-r md:rounded-none md:border-r-0",
    "hidden md:table-cell",
    "hidden md:table-cell",
    `hidden md:table-cell ${isAdmin ? "" : "rounded-r-card border-r"}`,
    ...(isAdmin ? ["rounded-r-card border-r"] : []),
  ];
  return (
    <tr className="shadow-sm">
      {cells.map((cls, index) => (
        <td key={index} className={`bg-white px-4 py-4 border-y border-slate-200 ${cls}`}>
          <div className="h-4 bg-slate-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );
});

// Debounce Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <section className="min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
          <p className="text-lg text-red-600 mb-4 font-oxanium">
            Error: {this.state.error.message}
          </p>
          <p className="text-sm text-slate-600">
            Please check the backend connection or server configuration.
          </p>
        </section>
      );
    }
    return this.props.children;
  }
}

const Startlist = () => {
  const { realEventId: eventId } = useEventId();
  const [state, setState] = useState({
    eventName: "Startlist",
    categories: [], // Dynamic Categories
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    filteredAllParticipants: [], // New state for full non-paginated matches
    totalByCategory: {},
    totalAll: 0,
    searchTerm: "",
    selectedCategory: "all",
    loading: true,
    loadingMore: false,
    error: null,
    hasMore: true,
    sortGenderDirection: null,
    searchPageIndex: 0,
  });

  const isMounted = useRef(true);
  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  // ── Finish-desk receipt ────────────────────────────────────────────────────
  // A runner arrives, says their bib, and the operator prints their time. The
  // search box above is already the fastest way to reach one bib, so printing
  // lives here rather than on a page of its own.
  //
  // Results stream in over onSnapshot (the same source the announcer board
  // uses), so a bib crossing the mat becomes printable within a second without
  // the operator reloading anything.
  const [finishByBib, setFinishByBib] = useState({});
  const [receipt, setReceipt] = useState(null);

  // Printing is an operator action: the page itself stays public so runners can
  // still look up the start list, but the button only exists for an admin.
  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (!eventId || !isAdmin) return undefined;
    const unsub = onSnapshot(
      collection(getFirestore(), "event_timing", eventId, "results"),
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const r = d.data();
          // checkpoints.finish is the authoritative crossing; finishMatTime is
          // the raw mat read. lastCheckpoint is NOT a finish — it is set by any
          // checkpoint, so a runner who only passed CP1 must not look finished.
          const instant = r.checkpoints?.finish?.time || r.finishMatTime || null;
          // A hand-timed (buzzer) result has no mat crossing at all: the
          // recorded duration IS the finish. Gating on the mat alone left every
          // such runner "Not finished" after the race and blocked the receipt.
          const recorded = r.recordedDuration || null;
          if (!instant && !recorded) return;
          next[String(r.bib ?? d.id)] = {
            // Sembalun is a gun start, so gunTime is the ranked time and the one
            // the runner is told. netTime only differs on individual starts.
            //
            // MYLAPS stores it at millisecond precision ("00:06:28.584"); the
            // runner is handed a receipt, not a data export, so the fraction is
            // dropped the way every other board here shows it.
            // Raw here; the precision shown is the category's (see shownElapsed).
            elapsed: recorded || r.finishTime || r.gunTime || r.netTime || null,
            instant,
            categoryId: r.categoryId || "",
            gender: (r.gender || "").toLowerCase(),
            // Ranking key at full precision, so two runners sharing a printed
            // second still rank in the order they crossed.
            sortKey: recorded || r.finishTime || r.gunTime || r.netTime || "",
          };
        });

        // genderRank / overallRank are computed by the results API at query
        // time and never stored on the document, so the desk derives them from
        // this same snapshot — one live source, no extra request, and the
        // receipt cannot disagree with the board.
        //
        // Ranked WITHIN a category: a 7K runner is not placed against the 42K
        // field. A missed start still ranks — gunTime is what counts here.
        const byCat = {};
        Object.entries(next).forEach(([bib, f]) => {
          (byCat[f.categoryId] = byCat[f.categoryId] || []).push({ bib, ...f });
        });
        Object.values(byCat).forEach((list) => {
          list.sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)));
          const seen = { male: 0, female: 0 };
          list.forEach((row, i) => {
            next[row.bib].overallRank = i + 1;
            if (row.gender === "male" || row.gender === "female") {
              seen[row.gender] += 1;
              next[row.bib].genderRank = seen[row.gender];
            } else {
              next[row.bib].genderRank = null;
            }
          });
        });

        if (isMounted.current) setFinishByBib(next);
      },
      () => {
        /* a dropped listener must not blank the desk: keep the last snapshot */
      }
    );
    return () => unsub();
  }, [eventId, isAdmin]);

  // Whole seconds by default, hundredths for a category that shows them.
  const shownElapsed = useCallback(
    (fin) =>
      displayElapsed(
        fin?.elapsed || "",
        state.categories.find((c) => c.id === fin?.categoryId) || null,
      ) || "-",
    [state.categories],
  );

  const openReceipt = useCallback(
    (participant) => {
      const fin = finishByBib[String(participant.bib)];
      if (!fin) return;
      setReceipt({
        eventName: state.eventName,
        bibName: (participant.name || "").toUpperCase(),
        bib: participant.bib,
        categoryName: participant.categoryName,
        finishTime: shownElapsed(fin),
        gender: fin.gender,
        genderRank: fin.genderRank,
        overallRank: fin.overallRank,
      });
    },
    [finishByBib, state.eventName]
  );

  // Cleanup on unmount
  useEffect(() => {
    window.scrollTo(0, 0);
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch Event Metadata + Participants
  const fetchEventAndParticipants = useCallback(async () => {
    try {
      // 1. Fetch Event Details form Firestore
      const db = getFirestore();
      const eventRef = doc(db, "event_timing", eventId);
      const eventSnap = await getDoc(eventRef);

      if (!eventSnap.exists()) {
        throw new Error("Event not found");
      }

      const eventData = eventSnap.data();
      const categories = eventData.categories || [];
      const eventName = eventData.eventName || "Event Startlist";

      // 2. Fetch Participants from API
      const response = await axios.get(
        `${API_BASE_URL}/api/timing/participants`,
        {
          params: { eventId: eventId },
          timeout: 10000,
        }
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Invalid response: Data is not JSON");
      }

      const participants = response.data.participants || [];
      
      // Calculate totals
      const totalByCategory = {};
      categories.forEach(cat => totalByCategory[cat.id] = 0);
      
      participants.forEach((participant) => {
        if (totalByCategory[participant.categoryId] !== undefined) {
          totalByCategory[participant.categoryId] += 1;
        } else {
             // Handle unknown categories if any
             totalByCategory["unknown"] = (totalByCategory["unknown"] || 0) + 1;
        }
      });

      const totalAll = participants.length;
      
      // Map category ID to Name for display if needed, but we store simple object for filtering
      const formattedParticipants = participants.map((p, index) => {
          const cat = categories.find(c => c.id === p.categoryId);
          const categoryName = cat ? cat.name : "Unknown Category";
          
          return {
            id: `${p.bib}-${index}`,
            name: p.name || "Name not available",
            gender: p.gender || "Unknown",
            categoryId: p.categoryId || "Unknown",
            categoryName: categoryName,
            bib: p.bib || "Unknown",
            nationality: p.nationality || "N/A",
            // Chip code dari CSV upload (kolom optional `chipcode`). Diperlukan
            // untuk sistem MYLAPS: decoder kirim `c=<chipcode>` di Passing message,
            // server resolve chip → BIB via `event_timing/{eventId}/chips/{CODE}`.
            chipcode: p.chipcode || p.chipCode || p.tag || null,
          };
      });

      // Sort by BIB number (numerically)
      formattedParticipants.sort((a, b) => {
        const bibA = parseInt(a.bib) || 0;
        const bibB = parseInt(b.bib) || 0;
        return bibA - bibB;
      });

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          eventName,
          categories: [{ id: "all", name: "All Categories" }, ...categories],
          allParticipants: formattedParticipants,
          participants: formattedParticipants.slice(0, PAGE_SIZE),
          filteredParticipants: formattedParticipants.slice(0, PAGE_SIZE),
          filteredAllParticipants: formattedParticipants, // Initialize full list
          totalByCategory,
          totalAll,
          loading: false,
          hasMore: formattedParticipants.length > PAGE_SIZE,
        }));
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Failed to load data: ${err.message}`,
          loading: false,
        }));
      }
    }
  }, [eventId]);

  // Load initial data
  useEffect(() => {
    fetchEventAndParticipants();
  }, [fetchEventAndParticipants]);

  // Handle category change
  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === state.selectedCategory) return;
      setState((prev) => ({
        ...prev,
        selectedCategory: categoryId,
        searchTerm: "",
        searchPageIndex: 0,
        hasMore: true,
      }));
    },
    [state.selectedCategory]
  );

  // Handle search and category filtering
  useEffect(() => {
    let filteredList = state.allParticipants;

    // Filter by category
    if (state.selectedCategory !== "all") {
      filteredList = filteredList.filter(
        (p) => p.categoryId === state.selectedCategory
      );
    }

    // Filter by search term
    if (debouncedSearchTerm) {
      filteredList = filteredList.filter((p) =>
        p.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase().trim()) ||
        (p.bib && p.bib.toString().includes(debouncedSearchTerm.trim()))
      );
    }

    setState((prev) => ({
      ...prev,
      filteredParticipants: filteredList.slice(0, PAGE_SIZE),
      filteredAllParticipants: filteredList, // Keep full list reference
      totalFiltered: filteredList.length,
      hasMore: filteredList.length > PAGE_SIZE,
      searchPageIndex: 0,
    }));
  }, [debouncedSearchTerm, state.allParticipants, state.selectedCategory]);

  // Load more participants
  const loadMore = useCallback(() => {
    setState((prev) => {
      const currentIndex = prev.searchPageIndex + 1;
      const startIndex = currentIndex * PAGE_SIZE;
      let filteredList = prev.allParticipants;

      // Filter by category
      if (prev.selectedCategory !== "all") {
        filteredList = filteredList.filter(
          (p) => p.categoryId === prev.selectedCategory
        );
      }

      // Filter by search term
      if (debouncedSearchTerm) {
        filteredList = filteredList.filter((p) =>
          p.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase().trim()) ||
          (p.bib && p.bib.toString().includes(debouncedSearchTerm.trim()))
        );
      }

      const additionalList = filteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalList.length) return { ...prev, hasMore: false };

      return {
        ...prev,
        filteredParticipants: [...prev.filteredParticipants, ...additionalList],
        hasMore: startIndex + PAGE_SIZE < filteredList.length,
        searchPageIndex: currentIndex,
      };
    });
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.searchPageIndex,
    state.selectedCategory,
  ]);

  // Handle gender sort toggle
  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "asc" ? "desc" : "asc";
    setState((prev) => {
      const sortedParticipants = [...prev.allParticipants].sort((a, b) => {
        if (newDirection === "asc") {
          return a.gender.localeCompare(b.gender);
        }
        return b.gender.localeCompare(a.gender);
      });
      let filteredList = sortedParticipants;

      if (prev.selectedCategory !== "all") {
        filteredList = filteredList.filter(
          (p) => p.categoryId === prev.selectedCategory
        );
      }

      if (debouncedSearchTerm) {
        filteredList = filteredList.filter((p) =>
          p.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase().trim()) ||
          (p.bib && p.bib.toString().includes(debouncedSearchTerm.trim()))
        );
      }

      return {
        ...prev,
        sortGenderDirection: newDirection,
        allParticipants: sortedParticipants,
        participants: sortedParticipants.slice(0, PAGE_SIZE),
        filteredParticipants: filteredList.slice(0, PAGE_SIZE),
        filteredAllParticipants: filteredList, // Keep full list reference
        totalFiltered: filteredList.length,
        hasMore: filteredList.length > PAGE_SIZE,
        searchPageIndex: 0,
      };
    });
  }, [state.sortGenderDirection, state.selectedCategory, debouncedSearchTerm]);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => ({
      ...prev,
      searchTerm: searchValue,
      searchPageIndex: 0,
    }));
  }, []);

  const rangeText = `${state.filteredParticipants.length} of ${
    debouncedSearchTerm || state.selectedCategory !== "all"
      ? state.totalFiltered
      : state.totalAll
  } Participants`;

  if (state.error) {
    return (
      <section className="min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-red-600 mb-4 font-oxanium">{state.error}</p>
      </section>
    );
  }

  return (
    <ErrorBoundary>
      <section className="min-h-screen mt-10 bg-white mb-10">
        <div className="relative z-1 container mx-auto px-4">
          <motion.div
            className="mb-4 flex flex-col max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg md:text-xl font-bold uppercase text-slate-900 text-center block md:hidden">
              {state.eventName}
            </h3>
            <h3 className="text-lg md:text-xl font-bold uppercase text-slate-900 md:text-right hidden md:block">
              {state.eventName}
            </h3>
            <div className="mt-4 flex flex-wrap justify-between items-center gap-2">
              <div className="flex flex-wrap gap-2">
                {state.categories.map((category) => (
                  <Button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    variant={state.selectedCategory === category.id ? "primary" : "secondary"}
                    color="slate"
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
              <InputField
                type="text"
                value={state.searchTerm}
                onChange={handleSearchChange}
                placeholder="Search BIB or Name"
                className="text-center shadow-sm"
                wrapperClassName="w-full md:w-64"
              />
            </div>
          </motion.div>

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
                <thead>
                  <tr>
                    {/* Name */}
                    <th className="bg-slate-50 px-3 md:px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] rounded-l-card border-y border-l border-slate-200">
                      Name
                    </th>
                    {/* Bib */}
                    {/* On a phone BIB is the last column only when there is no
                        Finish column after it (non-admins); rounding it for an
                        admin split each row into two cards. */}
                    <th className={`bg-slate-50 px-2 md:px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200 ${isAdmin ? "" : "rounded-r-card border-r md:rounded-none md:border-r-0"}`}>
                      Bib
                    </th>
                    {/* Nationality - hidden on mobile */}
                    <th className="hidden md:table-cell bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Nationality
                    </th>
                    {/* Gender - hidden on mobile */}
                    <th
                      className="hidden md:table-cell bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] cursor-pointer hover:text-slate-900 border-y border-slate-200"
                      onClick={handleGenderSortToggle}
                      aria-label="Sort by gender"
                    >
                      <span className="flex items-center justify-center">
                        Gender
                        {state.sortGenderDirection && (
                          <FontAwesomeIcon
                            icon={
                              state.sortGenderDirection === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1 text-slate-500"
                          />
                        )}
                      </span>
                    </th>

                    {/* Code - hidden on mobile; confirms the MYLAPS CSV import */}
                    <th className={`hidden md:table-cell bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200 ${isAdmin ? "" : "rounded-r-card border-r"}`}>
                      Code
                    </th>
                    {isAdmin && (
                      <th className="bg-slate-50 px-2 md:px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                        Finish
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {state.loading ? (
                    [...Array(5)].map((_, index) => <SkeletonRow key={index} isAdmin={isAdmin} />)
                  ) : state.filteredParticipants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAdmin ? 6 : 5}
                        className="bg-white px-4 py-6 text-center text-sm text-slate-600 rounded-card border border-slate-200"
                      >
                        {state.searchTerm
                          ? "No participants match the search criteria."
                          : state.selectedCategory !== "all"
                          ? "No participants registered for this category."
                          : "No participants registered."}
                      </td>
                    </tr>
                  ) : (
                    state.filteredParticipants.map((participant) => {
                      const { code: flagCode, display: nationalityDisplay } =
                        getFlagCode(participant.nationality);
                      return (
                        <tr
                          key={participant.id}
                          className="hover:-translate-y-[2px] transition-transform shadow-sm group"
                        >
                          {/* Name + chipcode (row kedua untuk konfirmasi import CSV MYLAPS) */}
                          <td
                            className={`bg-white px-3 md:px-4 py-4 text-sm font-bold uppercase max-w-[42vw] md:max-w-[200px] rounded-l-card border-y border-l border-slate-200 group-hover:bg-slate-50 transition-colors ${
                              participant.gender === "female"
                                ? "text-slate-900"
                                : "text-slate-600"
                            }`}
                          >
                            {/* Wraps instead of cutting: a doubles pair or a
                                five-person relay team is several names, and a
                                cut line showed only the first ones. */}
                            <div className="break-words">{participant.name.toUpperCase()}</div>
                          </td>
                          {/* Bib - always visible */}
                          <td className={`bg-white px-2 md:px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors ${isAdmin ? "" : "border-r md:border-r-0 rounded-r-card md:rounded-none"}`}>
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {participant.bib ? `BIB ${participant.bib}` : "-"}
                            </span>
                          </td>
                          {/* Nationality - desktop only */}
                          <td className="hidden md:table-cell bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              <img
                                src={`https://flagcdn.com/w20/${flagCode}.png`}
                                onError={(e) => {
                                  e.target.src =
                                    "https://flagcdn.com/w20/un.png";
                                }}
                                className="country-flag w-4 h-3 mr-1 border border-slate-200 rounded"
                                alt={nationalityDisplay}
                              />
                              {nationalityDisplay || "-"}
                            </span>
                          </td>
                          {/* Gender - desktop only */}
                          <td className="hidden md:table-cell bg-white px-4 py-4 text-sm text-slate-600 text-center max-w-[150px] border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {formatGender(participant.gender)}
                            </span>
                          </td>
                          {/* Code - desktop only; confirms the MYLAPS CSV import */}
                          <td className={`hidden md:table-cell bg-white px-4 py-4 text-sm font-mono text-slate-500 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors ${isAdmin ? "" : "rounded-r-card border-r"}`}>
                            {participant.chipcode || "-"}
                          </td>

                          {/* Finish time + receipt — operators only */}
                          {isAdmin && (
                            <td className="bg-white px-2 md:px-4 py-4 text-sm text-center rounded-r-card border-y border-r border-slate-200 group-hover:bg-slate-50 transition-colors md:whitespace-nowrap">
                              {finishByBib[String(participant.bib)] ? (
                                <>
                                  <span className="font-mono font-semibold text-slate-900 mr-2">
                                    {shownElapsed(finishByBib[String(participant.bib)])}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openReceipt(participant)}
                                    className="border border-slate-300 rounded-full px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-900 hover:text-white transition-colors"
                                  >
                                    Print
                                  </button>
                                </>
                              ) : (
                                // Saying why it cannot print beats a dead button:
                                // the runner is standing at the desk waiting.
                                <span className="text-xs text-slate-400">
                                  Not finished
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Load More */}
            <div className="flex justify-end items-center px-4 py-2">
              <span className="text-sm text-slate-600 mr-4">{rangeText}</span>
              <button
                onClick={loadMore}
                disabled={state.loading || !state.hasMore}
                className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                  ${
                    state.loading || !state.hasMore
                      ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                aria-label={
                  state.hasMore
                    ? "Load more"
                    : "End"
                }
              >
                <span>
                  {state.hasMore ? "Load More" : "End"}
                </span>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Receipt preview. The operator sees the exact 80mm slip before spending
          paper on it; the print stylesheet hides everything but the slip. */}
      {receipt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 print:bg-transparent print:p-0"
          onClick={() => setReceipt(null)}
        >
          <div
            className="bg-white rounded-card max-h-full overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Exactly one receipt in the tree: the print rule targets it by
                id, and a second copy would make the id ambiguous and print the
                slip twice. */}
            <div className="flex justify-center border-b border-slate-200 p-4">
              <FinishReceipt {...receipt} />
            </div>
            <div className="flex gap-2 p-4 print:hidden">
              <button
                type="button"
                onClick={() => setReceipt(null)}
                className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={printFinishReceipt}
                className="flex-1 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default Startlist;
